import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BlockIcon from "@mui/icons-material/Block";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import api from "../../../services/api";
import StatsCard from "../../../components/admin/StatsCard";
import EmptyState from "../../../components/admin/EmptyState";
import LoadingSkeleton from "../../../components/common/LoadingSkeleton";
import useAuthStore from "../../../store/authStore";
import useToastStore from "../../../store/toastStore";
import koreaRegions from "../../../data/koreaRegions.json";
import MarketApprovalTab from "../market/MarketApprovalTab";
import MarketExportItemsTab from "../market/MarketExportItemsTab";
import MarketStatsTab from "../market/MarketStatsTab";
import { addKoreaDaysString, getKoreaDateString } from "../../../utils/koreaDate";
import {
  calcTotalPrice,
  formatItemsText,
  formatPrice,
  getApiErrorMessage,
  getStatusChip,
} from "../market/marketUtils";
import { canManageDream } from "../../../utils/adminAccess";

const TIME_SLOTS = ["09:00-12:00", "12:00-15:00", "15:00-18:00", "18:00-21:00"];
const STATUSES = ["PENDING", "ASSIGNED", "ON_THE_WAY", "AWAITING_APPROVAL", "COMPLETED", "CANCELLED", "REJECTED"];
const CANDIDATE_STATES = ["OFFERED", "FAILED", "DECLINED", "EXPIRED", "ASSIGNED"];
const COLLECTOR_STATUSES = ["PENDING_APPROVAL", "APPROVED", "REJECTED", "SUSPENDED", "WITHDRAWN"];
const COLLECTOR_STATUS_LABELS = {
  PENDING_APPROVAL: "승인 대기",
  APPROVED: "승인 완료",
  REJECTED: "반려",
  SUSPENDED: "정지",
  WITHDRAWN: "탈퇴",
};
const DEFAULT_ITEM_CATEGORIES = ["주방/생활", "완구/유아", "침구/생활", "도서", "대형/유아", "기타"];

const todayString = () => getKoreaDateString();
const addDaysString = (days) => addKoreaDaysString(days);
const monthStartString = () => `${todayString().slice(0, 8)}01`;

const statusText = {
  PENDING: "미배정",
  ASSIGNED: "배정",
  ON_THE_WAY: "이동 중",
  AWAITING_APPROVAL: "승인 대기",
  COMPLETED: "완료",
  CANCELLED: "취소",
  REJECTED: "거절",
};

const candidateText = {
  OFFERED: "수락 대기",
  FAILED: "노출 실패",
  DECLINED: "거절 있음",
  EXPIRED: "만료",
  ASSIGNED: "배정 완료",
};

const requestCsvHeaders = [
  { label: "요청ID", key: "marketLocationId" },
  { label: "상태", key: "status" },
  { label: "희망일", key: "desiredDate" },
  { label: "시간대", key: "desiredTimeSlot" },
  { label: "지역", key: "region" },
  { label: "주소", key: "address" },
  { label: "상세주소", key: "detailAddress" },
  { label: "이용자", key: "userName" },
  { label: "이용자 연락처", key: "userPhoneNumber" },
  { label: "작업자", key: "collectorName" },
  { label: "품목", key: "items" },
  { label: "예상금액", key: "estimatedTotalPrice" },
  { label: "자동노출", key: "candidateState" },
  { label: "후보수", key: "candidateCount" },
  { label: "수락대기수", key: "offeredCount" },
  { label: "실패사유", key: "failureReason" },
  { label: "기한경과", key: "overdue" },
  { label: "무효처리", key: "adminVoided" },
  { label: "무효처리 사유", key: "adminVoidReason" },
  { label: "등록일", key: "createdAt" },
];

function defaultRequestFilters() {
  return {
    status: "ALL",
    fromDate: todayString(),
    toDate: addDaysString(30),
    timeSlot: "ALL",
    regionSido: "",
    regionSigungu: "",
    collectorId: "",
    userId: "",
    itemId: "",
    candidateState: "ALL",
    overdue: "",
  };
}

function requestQuickFilters(key) {
  const presets = {
    today: { fromDate: todayString(), toDate: todayString(), status: "ALL", candidateState: "ALL", overdue: "" },
    unassigned: { status: "PENDING", candidateState: "ALL", overdue: "" },
    waiting: { status: "PENDING", candidateState: "OFFERED", overdue: "" },
    approval: { status: "AWAITING_APPROVAL", candidateState: "ALL", overdue: "" },
    overdue: { overdue: "true", status: "ALL", candidateState: "ALL" },
    autoFailed: { status: "PENDING", candidateState: "FAILED", overdue: "" },
  };
  return presets[key] || null;
}

function requestQueryFilters(searchParams) {
  const allowed = [
    "status",
    "fromDate",
    "toDate",
    "timeSlot",
    "regionSido",
    "regionSigungu",
    "collectorId",
    "userId",
    "itemId",
    "candidateState",
    "overdue",
  ];
  return allowed.reduce((acc, key) => {
    if (searchParams.has(key)) acc[key] = searchParams.get(key) || "";
    return acc;
  }, {});
}

function buildRequestUrl(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `/admin/dream/requests?${query}` : "/admin/dream/requests";
}

function splitRegionLabel(regionLabel = "") {
  const match = regionOptions().find((region) => `${region.sido} ${region.sigungu}` === regionLabel);
  return match || { sido: "", sigungu: "" };
}

function queueLocation(row) {
  return row?.location || row || {};
}

function normalizeSearchValue(value) {
  return String(value ?? "").trim().toLowerCase();
}

function requestSearchText(row) {
  const loc = queueLocation(row);
  return [
    loc.marketLocationId,
    loc.address,
    loc.detailAddress,
    loc.regionSido,
    loc.regionSigungu,
    loc.userName,
    loc.userPhoneNumber,
    loc.collectorName,
    loc.desiredDate,
    loc.desiredTimeSlot,
    statusText[loc.status] || loc.status,
    candidateText[row.candidateState] || row.candidateState,
    formatItemsText(loc.items),
  ].map(normalizeSearchValue).join(" ");
}

function requestMatchesSearch(row, keyword) {
  const normalizedKeyword = normalizeSearchValue(keyword);
  if (!normalizedKeyword) return true;
  return requestSearchText(row).includes(normalizedKeyword);
}

function requestCsvRow(row) {
  const loc = queueLocation(row);
  return {
    marketLocationId: loc.marketLocationId || "",
    status: statusText[loc.status] || loc.status || "",
    desiredDate: loc.desiredDate || "",
    desiredTimeSlot: loc.desiredTimeSlot || "",
    region: `${loc.regionSido || ""} ${loc.regionSigungu || ""}`.trim(),
    address: loc.address || "",
    detailAddress: loc.detailAddress || "",
    userName: loc.userName || "",
    userPhoneNumber: loc.userPhoneNumber || "",
    collectorName: loc.collectorName || "",
    items: formatItemsText(loc.items),
    estimatedTotalPrice: calcTotalPrice(loc),
    candidateState: candidateText[row.candidateState] || row.candidateState || "",
    candidateCount: row.candidateCount ?? 0,
    offeredCount: row.offeredCount ?? 0,
    failureReason: row.failureReason || "",
    overdue: row.overdue ? "Y" : "N",
    adminVoided: row.adminVoided ? "Y" : "N",
    adminVoidReason: row.adminVoidReason || "",
    createdAt: loc.createdAt || "",
  };
}

function regionOptions() {
  return koreaRegions.flatMap((region) =>
    (region.sigungu || []).map((sigungu) => ({ sido: region.sido, sigungu }))
  );
}

function PageHeader({ title, description, action }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{title}</Typography>
        {description && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

function StatLink({ to, children }) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: "block",
        color: "inherit",
        textDecoration: "none",
        height: "100%",
        "& .MuiCard-root": {
          height: "100%",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        },
        "&:hover .MuiCard-root": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      {children}
    </Box>
  );
}

function StatButton({ selected = false, onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        height: "100%",
        p: 0,
        border: 0,
        bgcolor: "transparent",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        "& .MuiCard-root": {
          height: "100%",
          transition: "transform 0.15s ease, box-shadow 0.15s ease, outline-color 0.15s ease",
          outline: selected ? "2px solid" : "2px solid transparent",
          outlineColor: selected ? "primary.main" : "transparent",
        },
        "&:hover .MuiCard-root": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      {children}
    </Box>
  );
}

function QueueTable({
  rows,
  onRefreshOffers,
  onOpenAssign,
  onOpenDetail,
  onOpenDelete,
  onOpenVoid,
  compact = false,
  readOnly = false,
}) {
  if (!rows?.length) return <EmptyState message="표시할 요청이 없습니다." />;
  const showActions = onOpenDetail || (!readOnly && (onRefreshOffers || onOpenAssign || onOpenDelete || onOpenVoid));
  const clickableRows = Boolean(onOpenDetail);
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>요청</TableCell>
            <TableCell>상태</TableCell>
            <TableCell>지역/시간</TableCell>
            <TableCell>이용자</TableCell>
            <TableCell>작업자</TableCell>
            {!compact && <TableCell>품목/금액</TableCell>}
            <TableCell>자동 노출</TableCell>
            {showActions && <TableCell align="center">액션</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const loc = queueLocation(row);
            const detailPayload = { ...loc, ...row };
            return (
              <TableRow
                key={row.marketLocationId || loc.marketLocationId}
                hover
                onClick={clickableRows ? () => onOpenDetail(detailPayload) : undefined}
                sx={{ cursor: clickableRows ? "pointer" : "default" }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>#{loc.marketLocationId}</Typography>
                  <Typography variant="caption" color="text.secondary">{loc.address || "-"}</Typography>
                  {loc.detailAddress && <Typography variant="caption" color="text.secondary" display="block">{loc.detailAddress}</Typography>}
                  {row.overdue && <Chip size="small" color="error" variant="outlined" label="기한 경과" sx={{ mt: 0.5 }} />}
                  {row.adminVoided && <Chip size="small" color="error" label="무효 처리" sx={{ mt: 0.5, ml: row.overdue ? 0.5 : 0 }} />}
                </TableCell>
                <TableCell>
                  {getStatusChip(loc.status)}
                  {row.adminVoided && <Typography variant="caption" color="error" display="block">성과 제외</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{`${loc.regionSido || "-"} ${loc.regionSigungu || ""}`}</Typography>
                  <Typography variant="caption" color="text.secondary">{loc.desiredDate || "-"} · {loc.desiredTimeSlot || "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{loc.userName || "-"}</Typography>
                  <Typography variant="caption" color="text.secondary">{loc.userPhoneNumber || "배정 전 비공개"}</Typography>
                </TableCell>
                <TableCell>{loc.collectorName ? <Chip size="small" color="info" variant="outlined" label={loc.collectorName} /> : "미배정"}</TableCell>
                {!compact && (
                  <TableCell>
                    <Tooltip title={formatItemsText(loc.items)}>
                      <Typography variant="body2" sx={{ maxWidth: 220 }} noWrap>{formatItemsText(loc.items)}</Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">{formatPrice(calcTotalPrice(loc))}</Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Chip size="small" label={candidateText[row.candidateState] || row.candidateState || "-"} color={row.autoExposureFailed ? "error" : row.candidateState === "OFFERED" ? "warning" : "default"} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    후보 {row.candidateCount ?? 0} · 대기 {row.offeredCount ?? 0}
                  </Typography>
                  {row.failureReason && <Typography variant="caption" color="error" display="block">{row.failureReason}</Typography>}
                </TableCell>
                {showActions && (
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {onOpenDetail && (
                        <Tooltip title="상세 보기">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenDetail(detailPayload); }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && (
                      <Tooltip title="자동 노출 재시도">
                        <span onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRefreshOffers?.(loc.marketLocationId); }} disabled={!onRefreshOffers || loc.status !== "PENDING"}>
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                      {!readOnly && (
                      <Tooltip title="관리자 강제 배정">
                        <span onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onOpenAssign?.(loc); }} disabled={!onOpenAssign || !["PENDING", "ASSIGNED"].includes(loc.status)}>
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                      {!readOnly && onOpenDelete && (
                        <Tooltip title="요청 삭제">
                          <span onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => { e.stopPropagation(); onOpenDelete(loc); }}
                              disabled={["COMPLETED", "AWAITING_APPROVAL"].includes(loc.status)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {!readOnly && onOpenVoid && loc.status === "COMPLETED" && (
                        <Tooltip title={row.adminVoided ? "무효 처리 해제" : "완료 요청 무효 처리"}>
                          <span onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              color={row.adminVoided ? "success" : "warning"}
                              onClick={(e) => { e.stopPropagation(); onOpenVoid(detailPayload); }}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AssignDialog({ open, location, collectors, onClose, onSaved }) {
  const [collectorId, setCollectorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overrideWarning, setOverrideWarning] = useState(false);

  useEffect(() => {
    setCollectorId(location?.collectorId || "");
    setError("");
    setOverrideWarning(false);
  }, [location]);

  const save = async (allowBlockedTimeSlotOverride = false) => {
    if (!location || !collectorId) return;
    setLoading(true);
    setError("");
    try {
      await api.put(`/api/market-locations/${location.marketLocationId}/assign-collector`, {
        collectorId,
        allowBlockedTimeSlotOverride,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      const message = getApiErrorMessage(err, "배정에 실패했습니다.");
      if (!allowBlockedTimeSlotOverride && message.includes("예약이 차단")) {
        setOverrideWarning(true);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>관리자 강제 배정</DialogTitle>
      <DialogContent>
        {location && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              활동 지역, 승인 상태, 동일 시간대 lock 검증을 서버에서 다시 수행합니다.
            </Alert>
            <Typography variant="body2">
              #{location.marketLocationId} · {location.desiredDate || "-"} {location.desiredTimeSlot || "-"}
              <br />{location.address}
            </Typography>
            {overrideWarning && (
              <Alert severity="warning">
                선택한 날짜/시간대가 예약 불가 상태입니다. 운영상 필요한 경우에만 확인 후 배정하세요.
                작업자 승인, 활동 지역, 동일 시간대 중복 배정 검증은 계속 적용됩니다.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <FormControl size="small" fullWidth>
              <InputLabel>작업자</InputLabel>
              <Select value={collectorId} label="작업자" onChange={(e) => setCollectorId(e.target.value)}>
                <MenuItem value=""><em>선택</em></MenuItem>
                {(collectors || []).map((collector) => (
                  <MenuItem key={collector.userId} value={collector.userId}>
                    {collector.name} · {collector.accountStatus || "-"} · {collector.phoneNumber || "-"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={() => save(false)} disabled={loading || !collectorId}>배정</Button>
        {overrideWarning && (
          <Button color="warning" variant="contained" onClick={() => save(true)} disabled={loading || !collectorId}>
            예약 불가 확인 후 배정
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function RequestDetailDialog({ location, onClose }) {
  const itemText = location ? (location.itemsText || formatItemsText(location.items)) : "";
  const totalPrice = location ? (location.totalPrice ?? calcTotalPrice(location)) : 0;
  return (
    <Dialog open={!!location} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>요청 상세</DialogTitle>
      <DialogContent>
        {location && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>#{location.marketLocationId}</Typography>
                <Typography color="text.secondary">{location.address || "-"}</Typography>
                {location.detailAddress && <Typography color="text.secondary">{location.detailAddress}</Typography>}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {getStatusChip(location.status)}
                {location.adminVoided && <Chip size="small" color="error" label="무효 처리" />}
              </Stack>
            </Box>
            {location.adminVoided && (
              <Alert severity="error">
                이 완료 요청은 관리자/파트너에 의해 무효 처리되어 성과/금액 집계에서 제외됩니다.
                {location.adminVoidReason ? <><br />사유: {location.adminVoidReason}</> : null}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">희망 일정</Typography>
                <Typography>{location.desiredDate || "-"} · {location.desiredTimeSlot || "-"}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">지역</Typography>
                <Typography>{`${location.regionSido || "-"} ${location.regionSigungu || ""}`}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">예상 금액</Typography>
                <Typography>{formatPrice(totalPrice)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">이용자</Typography>
                <Typography>{location.userName || "-"} · {location.userPhoneNumber || "연락처 비공개"}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">작업자</Typography>
                <Typography>{location.collectorName || "미배정"}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">등록일</Typography>
                <Typography>{location.createdAt ? new Date(location.createdAt).toLocaleString("ko-KR") : "-"}</Typography>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>품목</Typography>
              <Typography>{itemText}</Typography>
            </Box>

            {location.memo && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>요청 메모</Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{location.memo}</Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteRequestDialog({ location, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setError("");
    setLoading(false);
    setConfirmText("");
    setReason("");
  }, [location]);

  const deleteRequest = async () => {
    if (!location) return;
    setLoading(true);
    setError("");
    try {
      await api.delete(`/api/market-locations/${location.marketLocationId}/admin`, {
        data: { reason },
      });
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "요청 삭제에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };
  const requiresConfirmText = ["ASSIGNED", "ON_THE_WAY", "REJECTED"].includes(location?.status);
  const canDelete = (!requiresConfirmText || confirmText.trim() === "삭제")
    && (!requiresConfirmText || reason.trim().length > 0);

  return (
    <Dialog open={!!location} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>수거 요청 삭제</DialogTitle>
      <DialogContent>
        {location && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              이 작업은 복구할 수 없습니다. 완료 건과 승인 대기 건은 삭제할 수 없으며, 필요한 경우 검수 처리 후 정리해야 합니다.
            </Alert>
            {requiresConfirmText && (
              <Alert severity="error">
                진행 이력이 있는 요청입니다. 작업자 배정, 이동 중, 재수거 이력이 있을 수 있으므로 운영상 삭제가 필요한 경우에만 진행하세요.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2">
              #{location.marketLocationId} · {location.desiredDate || "-"} {location.desiredTimeSlot || "-"}
              <br />{location.address || "-"}
              {location.detailAddress ? ` (${location.detailAddress})` : ""}
            </Typography>
            <Typography variant="body2">이용자: {location.userName || "-"}</Typography>
            <Typography variant="body2">상태: {statusText[location.status] || location.status || "-"}</Typography>
            {requiresConfirmText && (
              <TextField
                size="small"
                label="삭제 사유"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText="이 사유는 관리자 처리 이력에 저장됩니다."
              />
            )}
            {requiresConfirmText && (
              <TextField
                size="small"
                label="확인 입력"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                helperText="진행 이력이 있는 요청을 삭제하려면 '삭제'를 입력하세요."
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button color="error" variant="contained" onClick={deleteRequest} disabled={loading || !canDelete}>
          {loading ? "삭제 중..." : "삭제"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VoidRequestDialog({ location, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const isRestore = Boolean(location?.adminVoided);

  useEffect(() => {
    setError("");
    setLoading(false);
    setReason("");
  }, [location]);

  const save = async () => {
    if (!location) return;
    setLoading(true);
    setError("");
    try {
      const path = isRestore
        ? `/api/market-locations/${location.marketLocationId}/admin-void/restore`
        : `/api/market-locations/${location.marketLocationId}/admin-void`;
      await api.patch(path, { reason });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, isRestore ? "무효 처리 해제에 실패했습니다." : "무효 처리에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!location} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isRestore ? "무효 처리 해제" : "완료 요청 무효 처리"}</DialogTitle>
      <DialogContent>
        {location && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity={isRestore ? "info" : "warning"}>
              {isRestore
                ? "무효 처리를 해제하면 이 완료 건은 다시 성과/금액 집계에 포함됩니다."
                : "요청은 삭제되지 않고 COMPLETED 상태로 보존됩니다. 단, 관리자 리포트의 유효 완료/금액 집계에서는 제외됩니다."}
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2">
              #{location.marketLocationId} · {location.desiredDate || "-"} {location.desiredTimeSlot || "-"}
              <br />{location.address || "-"}
            </Typography>
            {location.adminVoided && (
              <Alert severity="error">
                기존 무효 사유: {location.adminVoidReason || "-"}
              </Alert>
            )}
            <TextField
              size="small"
              label={isRestore ? "해제 사유" : "무효 처리 사유"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              helperText="이 사유는 관리자 처리 이력에 저장됩니다."
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          color={isRestore ? "success" : "warning"}
          variant="contained"
          onClick={save}
          disabled={loading || !reason.trim()}
        >
          {loading ? "처리 중..." : isRestore ? "무효 해제" : "무효 처리"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function useCollectors() {
  const [collectors, setCollectors] = useState([]);
  const fetchCollectors = useCallback(async () => {
    const res = await api.get("/api/admin/collectors", { params: { accountStatus: "ALL" } });
    setCollectors(res.data || []);
  }, []);
  useEffect(() => { fetchCollectors().catch(() => setCollectors([])); }, [fetchCollectors]);
  return [collectors, fetchCollectors];
}

export function DreamOperationsPage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const showToast = useToastStore((s) => s.showToast);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collectors] = useCollectors();
  const [assignTarget, setAssignTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/market-locations/admin/operations-summary");
      setSummary(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const refreshOffers = async (id) => {
    try {
      await api.post(`/api/market-locations/${id}/refresh-offers`);
      showToast("자동 노출을 재시도했습니다.", "success");
      fetchSummary();
    } catch (err) {
      showToast(getApiErrorMessage(err, "자동 노출 재시도에 실패했습니다."), "error");
    }
  };

  if (loading) return <LoadingSkeleton variant="cards" />;
  const urgentQueue = summary?.urgentQueue || [];

  return (
    <Box>
      <PageHeader
        title="들어드림 운영 대시보드"
        description="오늘 처리해야 할 요청, 자동 노출 실패, 검수 대기, 작업자 승인 대기를 우선 표시합니다."
        action={<Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchSummary}>새로고침</Button>}
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=today"><StatsCard title="오늘 요청" value={`${summary?.todayRequests || 0}건`} icon={AssignmentIcon} color="#2a9ac6" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=unassigned"><StatsCard title="오늘 미배정" value={`${summary?.todayUnassigned || 0}건`} icon={PendingActionsIcon} color="#f39c12" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=waiting"><StatsCard title="수락 대기" value={`${summary?.acceptanceWaiting || 0}건`} icon={LocalShippingIcon} color="#1e88e5" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=approval"><StatsCard title="승인 대기" value={`${summary?.approvalWaiting || 0}건`} icon={FactCheckIcon} color="#8e44ad" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=overdue"><StatsCard title="기한 경과" value={`${summary?.overdue || 0}건`} icon={WarningIcon} color="#e74c3c" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/requests?quick=autoFailed"><StatsCard title="자동 노출 실패" value={`${summary?.autoExposureFailed || 0}건`} icon={AutorenewIcon} color="#d35400" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/collectors"><StatsCard title="작업자 승인 대기" value={`${summary?.collectorApprovalPending || 0}명`} icon={PersonAddIcon} color="#27ae60" /></StatLink></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatLink to="/admin/dream/schedule"><StatsCard title="오늘 슬롯" value={`${(summary?.timeSlotLoads || []).reduce((sum, slot) => sum + Number(slot.total || 0), 0)}건`} icon={CalendarMonthIcon} color="#34495e" /></StatLink></Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>긴급 처리 큐</Typography>
            <QueueTable
              rows={urgentQueue}
              compact
              onRefreshOffers={canMutate ? refreshOffers : null}
              onOpenAssign={canMutate ? setAssignTarget : null}
              onOpenDetail={setDetailTarget}
              onOpenDelete={canMutate ? setDeleteTarget : null}
              onOpenVoid={canMutate ? setVoidTarget : null}
              readOnly={!canMutate}
            />
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={{ mb: 3 }}><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>오늘 시간대별 작업량</Typography>
            <Stack spacing={1}>
              {(summary?.timeSlotLoads || []).map((slot) => (
                <Box
                  key={slot.timeSlot}
                  component={Link}
                  to={buildRequestUrl({ quick: "today", timeSlot: slot.timeSlot })}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    color: "inherit",
                    textDecoration: "none",
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                    "&:hover": { bgcolor: "action.hover", borderColor: "primary.light" },
                  }}
                >
                  <Typography>{slot.timeSlot}</Typography>
                  <Typography fontWeight={700}>{slot.total}건</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent></Card>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>오늘 지역별 요청 집중도</Typography>
            <Stack spacing={1}>
              {(summary?.regionalLoads || []).length === 0 && <Typography color="text.secondary">오늘 요청이 없습니다.</Typography>}
              {(summary?.regionalLoads || []).map((region) => {
                const parsedRegion = splitRegionLabel(region.region);
                return (
                  <Box
                    key={region.region}
                    component={Link}
                    to={buildRequestUrl({ quick: "today", regionSido: parsedRegion.sido, regionSigungu: parsedRegion.sigungu })}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1,
                      borderRadius: 1,
                      color: "inherit",
                      textDecoration: "none",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Typography>{region.region}</Typography>
                    <Chip size="small" label={`${region.count}건`} />
                  </Box>
                );
              })}
            </Stack>
          </CardContent></Card>
        </Grid>
      </Grid>
      {canMutate && (
        <>
          <AssignDialog open={!!assignTarget} location={assignTarget} collectors={collectors} onClose={() => setAssignTarget(null)} onSaved={fetchSummary} />
          <DeleteRequestDialog location={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={fetchSummary} />
          <VoidRequestDialog location={voidTarget} onClose={() => setVoidTarget(null)} onSaved={fetchSummary} />
        </>
      )}
      <RequestDetailDialog location={detailTarget} onClose={() => setDetailTarget(null)} />
    </Box>
  );
}

export function DreamRequestsPage({ initialTab = "queue" }) {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const showToast = useToastStore((s) => s.showToast);
  const [searchParams] = useSearchParams();
  const searchParamString = searchParams.toString();
  const quickParam = searchParams.get("quick");
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam === "matching" || tabParam === "queue" ? tabParam : initialTab);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectors] = useCollectors();
  const [assignTarget, setAssignTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [keyword, setKeyword] = useState(searchParams.get("q") || searchParams.get("requestId") || "");
  const [filters, setFilters] = useState(() => ({
    ...defaultRequestFilters(),
    ...(requestQuickFilters(quickParam) || {}),
    ...requestQueryFilters(searchParams),
  }));

  useEffect(() => {
    if (tabParam === "matching" || tabParam === "queue") {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  useEffect(() => {
    const nextSearchParams = new URLSearchParams(searchParamString);
    setFilters((prev) => ({
      ...prev,
      ...(requestQuickFilters(quickParam) || {}),
      ...requestQueryFilters(nextSearchParams),
    }));
    setKeyword(nextSearchParams.get("q") || nextSearchParams.get("requestId") || "");
  }, [quickParam, searchParamString]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== "ALL"));
      const res = await api.get("/api/market-locations/admin/request-queue", { params });
      setRows(res.data || []);
    } catch (err) {
      showToast("요청 큐를 불러오지 못했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const refreshOffers = async (id) => {
    try {
      await api.post(`/api/market-locations/${id}/refresh-offers`);
      showToast("자동 노출을 재시도했습니다.", "success");
      fetchRows();
    } catch (err) {
      showToast(getApiErrorMessage(err, "자동 노출 재시도에 실패했습니다."), "error");
    }
  };

  const quick = (keyOrFilters) => {
    const next = typeof keyOrFilters === "string"
      ? requestQuickFilters(keyOrFilters)
      : keyOrFilters;
    if (next) {
      setFilters((prev) => ({ ...prev, ...next }));
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((row) => requestMatchesSearch(row, keyword)),
    [rows, keyword]
  );
  const csvData = useMemo(() => filteredRows.map(requestCsvRow), [filteredRows]);

  return (
    <Box>
      <PageHeader title="요청 운영" description="수거 요청, 자동 노출, 매칭/배정 예외를 한 화면에서 처리합니다." />
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab value="queue" label="요청 큐" />
        <Tab value="matching" label="매칭/배정" />
      </Tabs>

      {activeTab === "matching" ? (
        <DreamMatchingPanel />
      ) : (
        <>
          <Card sx={{ mb: 2 }}><CardContent>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <Button size="small" variant="outlined" onClick={() => quick("today")}>오늘 작업</Button>
              <Button size="small" variant="outlined" onClick={() => quick("unassigned")}>미배정</Button>
              <Button size="small" variant="outlined" onClick={() => quick("waiting")}>수락 대기</Button>
              <Button size="small" variant="outlined" onClick={() => quick("approval")}>승인 대기</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => quick("overdue")}>기한 경과</Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => quick("autoFailed")}>자동 노출 실패</Button>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
              <TextField select size="small" label="상태" value={filters.status} onChange={(e) => quick({ status: e.target.value })}>
                <MenuItem value="ALL">전체</MenuItem>
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusText[s] || s}</MenuItem>)}
              </TextField>
              <TextField type="date" size="small" label="시작일" value={filters.fromDate} onChange={(e) => quick({ fromDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" label="종료일" value={filters.toDate} onChange={(e) => quick({ toDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField select size="small" label="시간대" value={filters.timeSlot} onChange={(e) => quick({ timeSlot: e.target.value })}>
                <MenuItem value="ALL">전체</MenuItem>
                {TIME_SLOTS.map((slot) => <MenuItem key={slot} value={slot}>{slot}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="지역" value={`${filters.regionSido}|${filters.regionSigungu}`} onChange={(e) => {
                const [sido, sigungu] = e.target.value.split("|");
                quick({ regionSido: sido || "", regionSigungu: sigungu || "" });
              }}>
                <MenuItem value="|">전체</MenuItem>
                {regionOptions().map((r) => <MenuItem key={`${r.sido}-${r.sigungu}`} value={`${r.sido}|${r.sigungu}`}>{r.sido} {r.sigungu}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="자동 노출" value={filters.candidateState} onChange={(e) => quick({ candidateState: e.target.value })}>
                <MenuItem value="ALL">전체</MenuItem>
                {CANDIDATE_STATES.map((s) => <MenuItem key={s} value={s}>{candidateText[s] || s}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="기한 경과" value={filters.overdue} onChange={(e) => quick({ overdue: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="true">기한 경과</MenuItem>
                <MenuItem value="false">정상</MenuItem>
              </TextField>
              <TextField
                type="number"
                size="small"
                label="작업자 ID"
                value={filters.collectorId}
                onChange={(e) => quick({ collectorId: e.target.value })}
                inputProps={{ min: 1 }}
              />
              <TextField
                type="number"
                size="small"
                label="이용자 ID"
                value={filters.userId}
                onChange={(e) => quick({ userId: e.target.value })}
                inputProps={{ min: 1 }}
              />
              <TextField
                type="number"
                size="small"
                label="품목 ID"
                value={filters.itemId}
                onChange={(e) => quick({ itemId: e.target.value })}
                inputProps={{ min: 1 }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows}>새로고침</Button>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 2 }}>
              <TextField
                size="small"
                label="이용자/주소/품목 검색"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 320 } }}
              />
              {csvData.length > 0 ? (
                <Button
                  variant="outlined"
                  component={CSVLink}
                  data={csvData}
                  headers={requestCsvHeaders}
                  filename={`dream_requests_${filters.fromDate || "all"}_${filters.toDate || "all"}.csv`}
                >
                  CSV 내보내기
                </Button>
              ) : (
                <Button variant="outlined" disabled>CSV 내보내기</Button>
              )}
              <Typography variant="body2" color="text.secondary">
                {keyword ? `${filteredRows.length} / ${rows.length}건 표시` : `${rows.length}건 표시`}
              </Typography>
            </Stack>
          </CardContent></Card>
          {loading ? <LoadingSkeleton variant="table" rows={8} /> : (
            <QueueTable
              rows={filteredRows}
              onRefreshOffers={canMutate ? refreshOffers : null}
              onOpenAssign={canMutate ? setAssignTarget : null}
              onOpenDetail={setDetailTarget}
              onOpenDelete={canMutate ? setDeleteTarget : null}
              onOpenVoid={canMutate ? setVoidTarget : null}
              readOnly={!canMutate}
            />
          )}
          {canMutate && (
            <>
              <AssignDialog open={!!assignTarget} location={assignTarget} collectors={collectors} onClose={() => setAssignTarget(null)} onSaved={fetchRows} />
              <DeleteRequestDialog location={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={fetchRows} />
              <VoidRequestDialog location={voidTarget} onClose={() => setVoidTarget(null)} onSaved={fetchRows} />
            </>
          )}
          <RequestDetailDialog location={detailTarget} onClose={() => setDetailTarget(null)} />
        </>
      )}
    </Box>
  );
}

function DreamMatchingPanel() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const showToast = useToastStore((s) => s.showToast);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectors] = useCollectors();
  const [assignTarget, setAssignTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/market-locations/admin/matching");
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const refreshOffers = async (id) => {
    try {
      await api.post(`/api/market-locations/${id}/refresh-offers`);
      showToast("후보 노출을 갱신했습니다.", "success");
      fetchRows();
    } catch (err) {
      showToast(getApiErrorMessage(err, "후보 노출 갱신에 실패했습니다."), "error");
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>매칭/배정 관리</Typography>
              <Typography variant="body2" color="text.secondary">
                지역 기반 자동 노출 결과와 수락/거절/미응답 상태를 확인하고 예외 건만 수동 개입합니다.
              </Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchRows}>새로고침</Button>
          </Stack>
        </CardContent>
      </Card>
      {loading ? <LoadingSkeleton variant="table" rows={8} /> : (
        <QueueTable
          rows={rows}
          onRefreshOffers={canMutate ? refreshOffers : null}
          onOpenAssign={canMutate ? setAssignTarget : null}
          onOpenDetail={setDetailTarget}
          onOpenDelete={canMutate ? setDeleteTarget : null}
          onOpenVoid={canMutate ? setVoidTarget : null}
          readOnly={!canMutate}
        />
      )}
      {canMutate && (
        <>
          <AssignDialog open={!!assignTarget} location={assignTarget} collectors={collectors} onClose={() => setAssignTarget(null)} onSaved={fetchRows} />
          <DeleteRequestDialog location={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={fetchRows} />
          <VoidRequestDialog location={voidTarget} onClose={() => setVoidTarget(null)} onSaved={fetchRows} />
        </>
      )}
      <RequestDetailDialog location={detailTarget} onClose={() => setDetailTarget(null)} />
    </Box>
  );
}

export function DreamMatchingPage() {
  return <DreamRequestsPage initialTab="matching" />;
}

export function DreamCollectorsPage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const showToast = useToastStore((s) => s.showToast);
  const [status, setStatus] = useState("PENDING_APPROVAL");
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ accountStatus: "APPROVED", hasVehicle: false, collectorRejectionReason: "", reason: "", serviceRegions: [] });

  const fetchCollectors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/collectors", { params: { accountStatus: status } });
      setCollectors(res.data || []);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchCollectors(); }, [fetchCollectors]);

  const open = async (collector) => {
    setTarget(collector);
    let regions = [];
    try {
      const res = await api.get(`/api/admin/collectors/${collector.userId}/service-regions`);
      regions = res.data || [];
    } catch (_) {}
    setForm({
      accountStatus: collector.accountStatus || "PENDING_APPROVAL",
      hasVehicle: Boolean(collector.hasVehicle),
      collectorRejectionReason: collector.collectorRejectionReason || "",
      reason: "",
      serviceRegions: regions,
    });
  };

  const saveApproval = async () => {
    if (!form.reason.trim()) {
      showToast("작업자 승인/상태 변경 사유를 입력해 주세요.", "warning");
      return;
    }
    try {
      await api.patch(`/api/admin/collectors/${target.userId}/approval`, {
        accountStatus: form.accountStatus,
        hasVehicle: form.hasVehicle,
        collectorRejectionReason: form.collectorRejectionReason,
        reason: form.reason,
      });
      showToast("작업자 승인 정보가 저장되었습니다.", "success");
      setTarget(null);
      fetchCollectors();
    } catch (err) {
      showToast(getApiErrorMessage(err, "작업자 승인 정보 저장에 실패했습니다."), "error");
    }
  };

  const saveRegions = async () => {
    try {
      const res = await api.put(`/api/admin/collectors/${target.userId}/service-regions`, form.serviceRegions);
      setForm((prev) => ({ ...prev, serviceRegions: res.data || [] }));
      showToast("활동 지역이 저장되었습니다.", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "활동 지역 저장에 실패했습니다."), "error");
    }
  };

  const addRegion = (value) => {
    const [sido, sigungu] = value.split("|");
    if (!sido || !sigungu) return;
    setForm((prev) => prev.serviceRegions.some((r) => r.sido === sido && r.sigungu === sigungu)
      ? prev
      : { ...prev, serviceRegions: [...prev.serviceRegions, { sido, sigungu }] });
  };

  return (
    <Box>
      <PageHeader
        title="작업자 승인/관리"
        description={canMutate
          ? "사업자 정보, 차량 여부, 활동 지역을 확인한 뒤 승인/반려/정지를 관리합니다."
          : "사업자 정보, 차량 여부, 활동 지역과 승인 상태를 조회합니다."}
      />
      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {COLLECTOR_STATUSES.map((s) => <Tab key={s} value={s} label={COLLECTOR_STATUS_LABELS[s]} />)}
      </Tabs>
      {loading ? <LoadingSkeleton variant="table" rows={6} /> : collectors.length === 0 ? <EmptyState message="작업자가 없습니다." /> : (
        <TableContainer component={Paper}><Table size="small">
          <TableHead><TableRow>
            <TableCell>신청일</TableCell><TableCell>작업자</TableCell><TableCell>사업자 정보</TableCell><TableCell>연락처</TableCell><TableCell>차량</TableCell><TableCell>상태</TableCell><TableCell align="center">{canMutate ? "심사" : "상세"}</TableCell>
          </TableRow></TableHead>
          <TableBody>{collectors.map((collector) => (
            <TableRow key={collector.userId} hover>
              <TableCell>{collector.createdAt ? new Date(collector.createdAt).toLocaleDateString("ko-KR") : "-"}</TableCell>
              <TableCell>{collector.name || "-"}<Typography variant="caption" color="text.secondary" display="block">{collector.email || "-"}</Typography></TableCell>
              <TableCell>{collector.businessName || "-"}<Typography variant="caption" display="block">{collector.businessNumber || "-"}</Typography></TableCell>
              <TableCell>{collector.phoneNumber || "-"}<Typography variant="caption" color={collector.phoneVerified ? "success.main" : "error"} display="block">{collector.phoneVerified ? "인증 완료" : "미인증"}</Typography></TableCell>
              <TableCell>{collector.hasVehicle ? "보유" : "미보유"}</TableCell>
              <TableCell><Chip size="small" label={COLLECTOR_STATUS_LABELS[collector.accountStatus] || collector.accountStatus || "-"} /></TableCell>
              <TableCell align="center"><Button size="small" variant={canMutate ? "contained" : "outlined"} onClick={() => open(collector)}>{canMutate ? "검토" : "보기"}</Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      )}

      <Dialog open={!!target} onClose={() => setTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>{canMutate ? "작업자 승인 검토" : "작업자 상세"}</DialogTitle>
        <DialogContent>
          {target && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={1}>
                  <Typography fontWeight={700}>{target.name}</Typography>
                  <Typography variant="body2">상호명: {target.businessName || "-"}</Typography>
                  <Typography variant="body2">사업자번호: {target.businessNumber || "-"}</Typography>
                  <Typography variant="body2">대표자명: {target.representativeName || "-"}</Typography>
                  <Typography variant="body2">연락처: {target.phoneNumber || "-"}</Typography>
                  {target.businessLicenseImageUrl ? (
                    <Box component="img" src={target.businessLicenseImageUrl} alt="사업자등록증" sx={{ width: "100%", maxHeight: 280, objectFit: "contain", bgcolor: "#f5f5f5", borderRadius: 1 }} />
                  ) : <Alert severity="warning">사업자등록증 이미지가 없습니다.</Alert>}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <TextField select label="승인 상태" value={form.accountStatus} onChange={(e) => setForm((p) => ({ ...p, accountStatus: e.target.value }))} disabled={!canMutate}>
                    {COLLECTOR_STATUSES.map((s) => <MenuItem key={s} value={s}>{COLLECTOR_STATUS_LABELS[s]}</MenuItem>)}
                  </TextField>
                  <FormControlLabel control={<Switch checked={form.hasVehicle} onChange={(e) => setForm((p) => ({ ...p, hasVehicle: e.target.checked }))} disabled={!canMutate} />} label="차량 보유" />
                  <TextField
                    label="변경 사유"
                    multiline
                    minRows={2}
                    value={form.reason}
                    onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                    disabled={!canMutate}
                    inputProps={{ maxLength: 500 }}
                    helperText="승인, 반려, 정지, 복구 사유가 처리 이력에 저장됩니다."
                  />
                  <TextField label="반려/정지 사유" multiline minRows={3} value={form.collectorRejectionReason} onChange={(e) => setForm((p) => ({ ...p, collectorRejectionReason: e.target.value }))} disabled={!canMutate} />
                  {canMutate && <TextField select label="활동 지역 추가" value="" onChange={(e) => addRegion(e.target.value)}>
                    <MenuItem value=""><em>선택</em></MenuItem>
                    {regionOptions().map((r) => <MenuItem key={`${r.sido}-${r.sigungu}`} value={`${r.sido}|${r.sigungu}`}>{r.sido} {r.sigungu}</MenuItem>)}
                  </TextField>}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {form.serviceRegions.map((r) => (
                      <Chip
                        key={`${r.sido}-${r.sigungu}`}
                        label={`${r.sido} ${r.sigungu}`}
                        onDelete={canMutate ? () => setForm((p) => ({ ...p, serviceRegions: p.serviceRegions.filter((x) => x.sido !== r.sido || x.sigungu !== r.sigungu) })) : undefined}
                      />
                    ))}
                    {form.serviceRegions.length === 0 && <Typography variant="caption" color="text.secondary">활동 지역이 없습니다.</Typography>}
                  </Stack>
                  {canMutate && <Button variant="outlined" onClick={saveRegions}>
                    활동 지역 저장
                  </Button>}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>취소</Button>
          {canMutate && <Button variant="contained" startIcon={<SaveIcon />} onClick={saveApproval}>승인 상태 저장</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function DreamSchedulePage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const [filters, setFilters] = useState({ fromDate: todayString(), toDate: addDaysString(6), regionSido: "", regionSigungu: "" });
  const [summary, setSummary] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedTimeSlots, setBlockedTimeSlots] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState(todayString());
  const [newBlockedDateReason, setNewBlockedDateReason] = useState("");
  const [newSlotDate, setNewSlotDate] = useState(todayString());
  const [newTimeSlot, setNewTimeSlot] = useState(TIME_SLOTS[0]);
  const [newSlotReason, setNewSlotReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotError, setSlotError] = useState("");
  const [dateError, setDateError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [collectors] = useCollectors();
  const [assignTarget, setAssignTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);

  const fetchBlockedDates = useCallback(async () => {
    const res = await api.get("/api/market-locations/blocked-dates");
    setBlockedDates(res.data || []);
  }, []);

  const fetchBlockedTimeSlots = useCallback(async () => {
    const res = await api.get("/api/market-locations/blocked-time-slots");
    setBlockedTimeSlots(res.data || []);
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/api/market-locations/admin/schedule", { params });
      setSummary(res.data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchBlockedDates().catch(() => setBlockedDates([])); }, [fetchBlockedDates]);
  useEffect(() => { fetchBlockedTimeSlots().catch(() => setBlockedTimeSlots([])); }, [fetchBlockedTimeSlots]);

  const refreshSchedule = async () => {
    await Promise.all([fetchSummary(), fetchBlockedDates(), fetchBlockedTimeSlots()]);
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate) return;
    setDateLoading(true);
    setDateError("");
    try {
      await api.post("/api/market-locations/blocked-dates", {
        date: newBlockedDate,
        reason: newBlockedDateReason,
      });
      setNewBlockedDateReason("");
      await refreshSchedule();
    } catch (err) {
      setDateError(getApiErrorMessage(err, "예약 불가 날짜 추가에 실패했습니다."));
    } finally {
      setDateLoading(false);
    }
  };

  const handleRemoveBlockedDate = async (date) => {
    setDateError("");
    try {
      await api.delete(`/api/market-locations/blocked-dates/${date}`);
      await refreshSchedule();
    } catch (err) {
      setDateError(getApiErrorMessage(err, "예약 불가 날짜 해제에 실패했습니다."));
    }
  };

  const handleAddBlockedTimeSlot = async () => {
    if (!newSlotDate || !newTimeSlot) return;
    setSlotLoading(true);
    setSlotError("");
    try {
      await api.post("/api/market-locations/blocked-time-slots", {
        date: newSlotDate,
        timeSlot: newTimeSlot,
        reason: newSlotReason,
      });
      setNewSlotReason("");
      await refreshSchedule();
    } catch (err) {
      setSlotError(getApiErrorMessage(err, "예약 불가 시간대 추가에 실패했습니다."));
    } finally {
      setSlotLoading(false);
    }
  };

  const handleRemoveBlockedTimeSlot = async (date, timeSlot) => {
    setSlotError("");
    try {
      await api.delete(`/api/market-locations/blocked-time-slots/${date}/${encodeURIComponent(timeSlot)}`);
      await refreshSchedule();
    } catch (err) {
      setSlotError(getApiErrorMessage(err, "예약 불가 시간대 해제에 실패했습니다."));
    }
  };

  const refreshOffers = async (id) => {
    try {
      await api.post(`/api/market-locations/${id}/refresh-offers`);
      await refreshSchedule();
    } catch (err) {
      setSlotError(getApiErrorMessage(err, "자동 노출 재시도에 실패했습니다."));
    }
  };

  const slots = summary?.slots || [];

  return (
    <Box>
      <PageHeader title="예약/시간대 설정" description="예약 차단, 시간대별 작업량, 작업자 중복 위험을 함께 확인합니다." />
      <Card sx={{ mb: 2 }}><CardContent>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          <TextField type="date" size="small" label="시작일" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="종료일" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField select size="small" label="지역" value={`${filters.regionSido}|${filters.regionSigungu}`} onChange={(e) => {
            const [sido, sigungu] = e.target.value.split("|");
            setFilters((p) => ({ ...p, regionSido: sido || "", regionSigungu: sigungu || "" }));
          }} sx={{ minWidth: 240 }}>
            <MenuItem value="|">전체</MenuItem>
            {regionOptions().map((r) => <MenuItem key={`${r.sido}-${r.sigungu}`} value={`${r.sido}|${r.sigungu}`}>{r.sido} {r.sigungu}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSchedule}>새로고침</Button>
        </Stack>
      </CardContent></Card>

      {canMutate && <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <BlockIcon color="warning" /> 날짜 전체 예약 불가 추가
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              선택한 날짜의 모든 신규 예약을 막습니다. 기존 요청은 자동 취소되지 않습니다.
            </Typography>
            <Stack spacing={1.5}>
              <TextField type="date" size="small" label="예약 불가 날짜" value={newBlockedDate}
                onChange={(e) => setNewBlockedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayString() }} />
              <TextField size="small" label="사유 (선택)" value={newBlockedDateReason}
                onChange={(e) => setNewBlockedDateReason(e.target.value)}
                placeholder="예: 전체 휴무, 작업자 부족" />
              <Button variant="contained" color="warning" startIcon={<BlockIcon />}
                onClick={handleAddBlockedDate}
                disabled={!newBlockedDate || dateLoading}>
                {dateLoading ? "설정 중..." : "날짜 전체 예약 불가"}
              </Button>
              {dateError && <Alert severity="error">{dateError}</Alert>}
            </Stack>
          </CardContent></Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthIcon color="warning" /> 예약 불가 날짜
            </Typography>
            {blockedDates.length === 0 ? (
              <EmptyState message="예약 불가 날짜가 없습니다." icon={CalendarMonthIcon} />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>날짜</TableCell><TableCell>사유</TableCell><TableCell align="center">해제</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {[...blockedDates]
                      .sort((a, b) => ((a.blockedDate || "") > (b.blockedDate || "") ? 1 : -1))
                      .map((date) => (
                        <TableRow key={date.blockedDate} hover>
                          <TableCell><Chip size="small" color="warning" variant="outlined" label={date.blockedDate} /></TableCell>
                          <TableCell>{date.reason || "-"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="예약 불가 날짜 해제">
                              <IconButton size="small" color="warning" onClick={() => handleRemoveBlockedDate(date.blockedDate)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>}

      {canMutate && <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <BlockIcon color="warning" /> 시간대 예약 불가 추가
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              날짜 전체가 아니라 특정 3시간 슬롯만 신규 예약을 막습니다. 기존 요청은 자동 취소되지 않습니다.
            </Typography>
            <Stack spacing={1.5}>
              <TextField type="date" size="small" label="예약 불가 날짜" value={newSlotDate}
                onChange={(e) => setNewSlotDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayString() }} />
              <FormControl size="small" fullWidth>
                <InputLabel>시간대</InputLabel>
                <Select value={newTimeSlot} label="시간대" onChange={(e) => setNewTimeSlot(e.target.value)}>
                  {TIME_SLOTS.map((slot) => <MenuItem key={slot} value={slot}>{slot}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="사유 (선택)" value={newSlotReason}
                onChange={(e) => setNewSlotReason(e.target.value)}
                placeholder="예: 작업자 부족, 특정 시간대 휴무" />
              <Button variant="contained" color="warning" startIcon={<BlockIcon />}
                onClick={handleAddBlockedTimeSlot}
                disabled={!newSlotDate || !newTimeSlot || slotLoading}>
                {slotLoading ? "설정 중..." : "시간대 예약 불가"}
              </Button>
              {slotError && <Alert severity="error">{slotError}</Alert>}
            </Stack>
          </CardContent></Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthIcon color="warning" /> 예약 불가 시간대
            </Typography>
            {blockedTimeSlots.length === 0 ? (
              <EmptyState message="예약 불가 시간대가 없습니다." icon={CalendarMonthIcon} />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>날짜</TableCell><TableCell>시간대</TableCell><TableCell>사유</TableCell><TableCell align="center">해제</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {[...blockedTimeSlots]
                      .sort((a, b) => (`${a.blockedDate} ${a.desiredTimeSlot}` > `${b.blockedDate} ${b.desiredTimeSlot}` ? 1 : -1))
                      .map((slot) => (
                        <TableRow key={`${slot.blockedDate}-${slot.desiredTimeSlot}`} hover>
                          <TableCell><Chip size="small" color="warning" variant="outlined" label={slot.blockedDate} /></TableCell>
                          <TableCell><Chip size="small" color="warning" label={slot.desiredTimeSlot} /></TableCell>
                          <TableCell>{slot.reason || "-"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="예약 불가 시간대 해제">
                              <IconButton size="small" color="warning" onClick={() => handleRemoveBlockedTimeSlot(slot.blockedDate, slot.desiredTimeSlot)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>}

      {!canMutate && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: "100%" }}><CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon color="warning" /> 예약 불가 날짜
              </Typography>
              {blockedDates.length === 0 ? (
                <EmptyState message="예약 불가 날짜가 없습니다." icon={CalendarMonthIcon} />
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>날짜</TableCell><TableCell>사유</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...blockedDates]
                        .sort((a, b) => ((a.blockedDate || "") > (b.blockedDate || "") ? 1 : -1))
                        .map((date) => (
                          <TableRow key={date.blockedDate} hover>
                            <TableCell><Chip size="small" color="warning" variant="outlined" label={date.blockedDate} /></TableCell>
                            <TableCell>{date.reason || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: "100%" }}><CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon color="warning" /> 예약 불가 시간대
              </Typography>
              {blockedTimeSlots.length === 0 ? (
                <EmptyState message="예약 불가 시간대가 없습니다." icon={CalendarMonthIcon} />
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>날짜</TableCell><TableCell>시간대</TableCell><TableCell>사유</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...blockedTimeSlots]
                        .sort((a, b) => (`${a.blockedDate} ${a.desiredTimeSlot}` > `${b.blockedDate} ${b.desiredTimeSlot}` ? 1 : -1))
                        .map((slot) => (
                          <TableRow key={`${slot.blockedDate}-${slot.desiredTimeSlot}`} hover>
                            <TableCell><Chip size="small" color="warning" variant="outlined" label={slot.blockedDate} /></TableCell>
                            <TableCell><Chip size="small" color="warning" label={slot.desiredTimeSlot} /></TableCell>
                            <TableCell>{slot.reason || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {loading ? <LoadingSkeleton variant="table" rows={8} /> : (
        <TableContainer component={Paper}><Table size="small">
          <TableHead><TableRow>
            <TableCell>날짜</TableCell><TableCell>시간대</TableCell><TableCell align="right">전체</TableCell><TableCell align="right">미배정</TableCell><TableCell align="right">배정</TableCell><TableCell align="right">이동/검수</TableCell><TableCell align="right">완료</TableCell><TableCell align="right">기한경과</TableCell><TableCell align="center">상세</TableCell>
          </TableRow></TableHead>
          <TableBody>{slots.map((slot) => (
            <TableRow
              key={`${slot.date}-${slot.timeSlot}`}
              hover
              onClick={slot.total ? () => setDialog(slot) : undefined}
              sx={{
                ...(slot.overdue > 0 || slot.pending > 0 ? { backgroundColor: "rgba(243,156,18,0.05)" } : {}),
                cursor: slot.total ? "pointer" : "default",
              }}
            >
              <TableCell>{slot.date}</TableCell><TableCell>{slot.timeSlot}</TableCell><TableCell align="right">{slot.total}</TableCell><TableCell align="right">{slot.pending}</TableCell><TableCell align="right">{slot.assigned}</TableCell><TableCell align="right">{Number(slot.onTheWay || 0) + Number(slot.awaitingApproval || 0)}</TableCell><TableCell align="right">{slot.completed}</TableCell><TableCell align="right">{slot.overdue}</TableCell>
              <TableCell align="center"><Button size="small" onClick={(e) => { e.stopPropagation(); setDialog(slot); }} disabled={!slot.total}>보기</Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      )}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{dialog?.date} {dialog?.timeSlot} 슬롯</DialogTitle>
        <DialogContent>
          {dialog && (
            <QueueTable
              rows={dialog.locations || []}
              compact
              onRefreshOffers={canMutate ? refreshOffers : null}
              onOpenAssign={canMutate ? setAssignTarget : null}
              onOpenDetail={setDetailTarget}
              onOpenDelete={canMutate ? setDeleteTarget : null}
              onOpenVoid={canMutate ? setVoidTarget : null}
              readOnly={!canMutate}
            />
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialog(null)}>닫기</Button></DialogActions>
      </Dialog>
      {canMutate && (
        <>
          <AssignDialog open={!!assignTarget} location={assignTarget} collectors={collectors} onClose={() => setAssignTarget(null)} onSaved={refreshSchedule} />
          <DeleteRequestDialog location={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={refreshSchedule} />
          <VoidRequestDialog location={voidTarget} onClose={() => setVoidTarget(null)} onSaved={refreshSchedule} />
        </>
      )}
      <RequestDetailDialog location={detailTarget} onClose={() => setDetailTarget(null)} />
    </Box>
  );
}

export function DreamInspectionPage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const [tab, setTab] = useState("approval");
  return (
    <Box>
      <PageHeader title="완료 검수/수출 품목" description="사진 비교 검수와 수출 품목 코드화를 승인 전 필수 단계로 처리합니다." />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="approval" label="완료 검수" />
        <Tab value="exports" label="수출 품목 기록" />
      </Tabs>
      {tab === "approval" ? <MarketApprovalTab readOnly={!canMutate} /> : <MarketExportItemsTab />}
    </Box>
  );
}

export function DreamItemsPage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const showToast = useToastStore((s) => s.showToast);
  const [items, setItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/item-catalog/admin");
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const categoryOptions = useMemo(() => {
    const values = new Set(DEFAULT_ITEM_CATEGORIES);
    items.forEach((item) => {
      if (item.category) values.add(item.category);
    });
    customCategories.forEach((category) => {
      if (category) values.add(category);
    });
    if (target?.category) values.add(target.category);
    return Array.from(values).filter(Boolean);
  }, [items, customCategories, target?.category]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory = categoryFilter === "ALL" || item.category === categoryFilter;
      const matchesActive =
        activeFilter === "ALL"
        || (activeFilter === "ACTIVE" && item.active !== false)
        || (activeFilter === "INACTIVE" && item.active === false);
      return matchesCategory && matchesActive;
    });
  }, [items, categoryFilter, activeFilter]);

  const openNew = () => setTarget({ category: DEFAULT_ITEM_CATEGORIES[0], name: "", price: 2500, specification: "75L 또는 100L 종량제 봉투 기준", optionDescription: "음식물 포함 불가", exportAvailable: true, active: true, sortOrder: 0, foodWasteProhibited: true });

  const addCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      showToast("추가할 카테고리명을 입력해주세요.", "warning");
      return;
    }
    setCustomCategories((prev) => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    setTarget((prev) => prev ? { ...prev, category: trimmed } : prev);
    setNewCategoryName("");
    setCategoryDialogOpen(false);
  };

  const save = async () => {
    try {
      if (target.itemId) await api.put(`/api/item-catalog/admin/${target.itemId}`, target);
      else await api.post("/api/item-catalog/admin", target);
      showToast("품목 정보가 저장되었습니다.", "success");
      setTarget(null);
      fetchItems();
    } catch (err) {
      showToast(getApiErrorMessage(err, "품목 저장에 실패했습니다."), "error");
    }
  };

  return (
    <Box>
      <PageHeader
        title="품목/가격 관리"
        description={canMutate
          ? "앱 노출 품목, 1건당 가격, 수출 코드, 사용 여부와 기준 설명을 관리합니다."
          : "앱 노출 품목, 1건당 가격, 수출 코드, 사용 여부와 기준 설명을 조회합니다."}
        action={canMutate ? <Button variant="contained" onClick={openNew}>품목 추가</Button> : null}
      />
      <Alert severity="info" sx={{ mb: 2 }}>
        기존 요청과 완료 기록에 사용된 품목은 삭제하지 않고 앱 노출을 꺼서 중단합니다. 가격 변경은 변경 이후 생성되는 요청에 적용됩니다.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
            <TextField
              select
              size="small"
              label="카테고리"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="ALL">전체</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="앱 노출"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="ALL">전체</MenuItem>
              <MenuItem value="ACTIVE">사용</MenuItem>
              <MenuItem value="INACTIVE">미사용</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {filteredItems.length} / {items.length}개 표시
            </Typography>
          </Stack>
        </CardContent>
      </Card>
      {loading ? <LoadingSkeleton variant="table" rows={6} /> : (
        <TableContainer component={Paper}><Table size="small">
          <TableHead><TableRow><TableCell>정렬</TableCell><TableCell>품목</TableCell><TableCell>가격</TableCell><TableCell>수출 코드</TableCell><TableCell>기준</TableCell><TableCell>상태</TableCell><TableCell align="center">{canMutate ? "수정" : "상세"}</TableCell></TableRow></TableHead>
          <TableBody>{filteredItems.map((item) => (
            <TableRow key={item.itemId} hover>
              <TableCell>{item.sortOrder ?? 0}</TableCell>
              <TableCell>{item.name}<Typography variant="caption" color="text.secondary" display="block">{item.category}</Typography></TableCell>
              <TableCell>{formatPrice(item.price)}</TableCell>
              <TableCell><Chip size="small" label={item.exportCode || "-"} /></TableCell>
              <TableCell>{item.specification || "-"}<Typography variant="caption" color="text.secondary" display="block">{item.optionDescription || ""}</Typography></TableCell>
              <TableCell><Stack direction="row" spacing={0.5}><Chip size="small" color={item.active ? "success" : "default"} label={item.active ? "사용" : "미사용"} /><Chip size="small" color={item.exportAvailable ? "info" : "default"} label={item.exportAvailable ? "수출 가능" : "수출 불가"} /></Stack></TableCell>
              <TableCell align="center"><Button size="small" onClick={() => setTarget(item)}>{canMutate ? "수정" : "보기"}</Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      )}
      <Dialog open={!!target} onClose={() => setTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{target?.itemId ? "품목 수정" : "품목 추가"}</DialogTitle>
        <DialogContent>
          {target && <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="품목명" value={target.name || ""} onChange={(e) => setTarget((p) => ({ ...p, name: e.target.value }))} disabled={!canMutate} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
              <TextField select label="카테고리" value={target.category || ""} onChange={(e) => setTarget((p) => ({ ...p, category: e.target.value }))} disabled={!canMutate}>
                {categoryOptions.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </TextField>
              {canMutate && <Button variant="outlined" onClick={() => setCategoryDialogOpen(true)}>
                카테고리 추가
              </Button>}
            </Box>
            <TextField label="가격" type="number" value={target.price ?? 2500} onChange={(e) => setTarget((p) => ({ ...p, price: Number(e.target.value) }))} disabled={!canMutate} />
            <TextField label="수출 코드" value={target.exportCode || ""} onChange={(e) => setTarget((p) => ({ ...p, exportCode: e.target.value }))} disabled={!canMutate} />
            <TextField label="건당 기준 설명" value={target.specification || ""} onChange={(e) => setTarget((p) => ({ ...p, specification: e.target.value }))} disabled={!canMutate} />
            <TextField label="운영 설명" value={target.optionDescription || ""} onChange={(e) => setTarget((p) => ({ ...p, optionDescription: e.target.value }))} disabled={!canMutate} />
            <TextField label="정렬 순서" type="number" value={target.sortOrder ?? 0} onChange={(e) => setTarget((p) => ({ ...p, sortOrder: Number(e.target.value) }))} disabled={!canMutate} />
            <FormControlLabel control={<Switch checked={target.active !== false} onChange={(e) => setTarget((p) => ({ ...p, active: e.target.checked }))} disabled={!canMutate} />} label="앱 노출" />
            <FormControlLabel control={<Switch checked={target.exportAvailable !== false} onChange={(e) => setTarget((p) => ({ ...p, exportAvailable: e.target.checked }))} disabled={!canMutate} />} label="수출 가능" />
            <FormControlLabel control={<Switch checked={target.foodWasteProhibited !== false} onChange={(e) => setTarget((p) => ({ ...p, foodWasteProhibited: e.target.checked }))} disabled={!canMutate} />} label="음식물 포함 불가" />
          </Stack>}
        </DialogContent>
        <DialogActions><Button onClick={() => setTarget(null)}>닫기</Button>{canMutate && <Button variant="contained" startIcon={<SaveIcon />} onClick={save}>저장</Button>}</DialogActions>
      </Dialog>
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>카테고리 추가</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="카테고리명"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCategory();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={addCategory}>추가</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function DreamCollectorWorkPage() {
  const showToast = useToastStore((s) => s.showToast);
  const [filters, setFilters] = useState({
    fromDate: monthStartString(),
    toDate: todayString(),
    collectorId: "",
    status: "",
    regionSido: "",
    regionSigungu: "",
    timeSlot: "",
  });
  const [selectedCollectorId, setSelectedCollectorId] = useState("");
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statMode, setStatMode] = useState("all");
  const [detailTarget, setDetailTarget] = useState(null);

  const cleanParams = (source) => Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== "" && value !== null && value !== undefined)
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const summaryParams = cleanParams(filters);
      const detailParams = cleanParams({
        ...filters,
        collectorId: selectedCollectorId || filters.collectorId,
      });
      const [summaryRes, itemsRes] = await Promise.all([
        api.get("/api/market-locations/admin/collector-work-summary", { params: summaryParams }),
        api.get("/api/market-locations/admin/collector-work-items", { params: detailParams }),
      ]);
      setSummary(summaryRes.data || []);
      setItems(itemsRes.data || []);
    } catch (err) {
      showToast(getApiErrorMessage(err, "작업자 작업 현황을 불러오지 못했습니다."), "error");
    } finally {
      setLoading(false);
    }
  }, [filters, selectedCollectorId, showToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const selectedCollector = useMemo(
    () => summary.find((row) => String(row.collectorId) === String(selectedCollectorId || filters.collectorId)),
    [summary, selectedCollectorId, filters.collectorId]
  );

  const totals = useMemo(() => summary.reduce((acc, row) => ({
    totalAssigned: acc.totalAssigned + Number(row.totalAssigned || 0),
    activeCount: acc.activeCount + Number(row.activeCount || 0),
    awaitingApprovalCount: acc.awaitingApprovalCount + Number(row.awaitingApprovalCount || 0),
    completedCount: acc.completedCount + Number(row.completedCount || 0),
    overdueCount: acc.overdueCount + Number(row.overdueCount || 0),
    totalRevenue: acc.totalRevenue + Number(row.totalRevenue || 0),
  }), {
    totalAssigned: 0,
    activeCount: 0,
    awaitingApprovalCount: 0,
    completedCount: 0,
    overdueCount: 0,
    totalRevenue: 0,
  }), [summary]);

  const displayedItems = useMemo(() => {
    if (statMode === "active") {
      return items.filter((item) => ["ASSIGNED", "ON_THE_WAY", "AWAITING_APPROVAL"].includes(item.status));
    }
    if (statMode === "awaitingApproval") {
      return items.filter((item) => item.status === "AWAITING_APPROVAL");
    }
    if (statMode === "completed") {
      return items.filter((item) => item.status === "COMPLETED" && !item.adminVoided);
    }
    if (statMode === "overdue") {
      return items.filter((item) => item.overdue);
    }
    return items;
  }, [items, statMode]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "collectorId") {
      setSelectedCollectorId(value);
    }
  };

  const setDateRange = (range) => {
    if (range === "today") {
      setFilters((prev) => ({ ...prev, fromDate: todayString(), toDate: todayString() }));
      return;
    }
    if (range === "month") {
      setFilters((prev) => ({ ...prev, fromDate: monthStartString(), toDate: todayString() }));
      return;
    }
    setFilters((prev) => ({ ...prev, fromDate: addDaysString(-29), toDate: todayString() }));
  };

  const csvHeaders = [
    { label: "요청번호", key: "marketLocationId" },
    { label: "작업자", key: "collectorName" },
    { label: "상태", key: "statusLabel" },
    { label: "희망일", key: "desiredDate" },
    { label: "시간대", key: "desiredTimeSlot" },
    { label: "지역", key: "region" },
    { label: "주소", key: "address" },
    { label: "이용자", key: "userName" },
    { label: "품목", key: "itemsText" },
    { label: "금액", key: "totalPrice" },
  ];
  const csvData = displayedItems.map((item) => ({
    ...item,
    statusLabel: statusText[item.status] || item.status,
    region: `${item.regionSido || ""} ${item.regionSigungu || ""}`.trim(),
  }));

  return (
    <Box>
      <PageHeader
        title="작업자 작업 현황"
        description="작업자별 배정, 진행, 검수 대기, 완료, 기한 경과와 상세 작업 내역을 기간 기준으로 확인합니다."
        action={
          <Stack direction="row" spacing={1}>
            {displayedItems.length > 0 && (
              <Button
                component={CSVLink}
                data={csvData}
                headers={csvHeaders}
                filename={`dream_collector_work_${filters.fromDate}_${filters.toDate}.csv`}
                variant="outlined"
              >
                CSV
              </Button>
            )}
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchRows}>새로고침</Button>
          </Stack>
        }
      />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={() => setDateRange("today")}>오늘</Button>
              <Button size="small" variant="outlined" onClick={() => setDateRange("month")}>이번 달</Button>
              <Button size="small" variant="outlined" onClick={() => setDateRange("30days")}>최근 30일</Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField size="small" type="date" label="시작일" fullWidth InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={(e) => updateFilter("fromDate", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField size="small" type="date" label="종료일" fullWidth InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={(e) => updateFilter("toDate", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField select size="small" label="작업자" fullWidth value={filters.collectorId} onChange={(e) => updateFilter("collectorId", e.target.value)}>
                  <MenuItem value="">전체</MenuItem>
                  {summary.map((row) => (
                    <MenuItem key={row.collectorId} value={row.collectorId}>{row.collectorName || `#${row.collectorId}`}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField select size="small" label="상태" fullWidth value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
                  <MenuItem value="">전체</MenuItem>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusText[s] || s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField select size="small" label="시간대" fullWidth value={filters.timeSlot} onChange={(e) => updateFilter("timeSlot", e.target.value)}>
                  <MenuItem value="">전체</MenuItem>
                  {TIME_SLOTS.map((slot) => <MenuItem key={slot} value={slot}>{slot}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  select
                  size="small"
                  label="지역"
                  fullWidth
                  value={filters.regionSido && filters.regionSigungu ? `${filters.regionSido}|${filters.regionSigungu}` : ""}
                  onChange={(e) => {
                    const [sido, sigungu] = e.target.value ? e.target.value.split("|") : ["", ""];
                    setFilters((prev) => ({ ...prev, regionSido: sido, regionSigungu: sigungu }));
                  }}
                >
                  <MenuItem value="">전체</MenuItem>
                  {regionOptions().map((r) => <MenuItem key={`${r.sido}-${r.sigungu}`} value={`${r.sido}|${r.sigungu}`}>{r.sido} {r.sigungu}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 2 }}><StatButton selected={statMode === "all"} onClick={() => setStatMode("all")}><StatsCard title="총 배정" value={`${totals.totalAssigned}건`} icon={AssignmentIcon} color="#2a9ac6" /></StatButton></Grid>
        <Grid size={{ xs: 12, md: 2 }}><StatButton selected={statMode === "active"} onClick={() => setStatMode("active")}><StatsCard title="진행 중" value={`${totals.activeCount}건`} icon={LocalShippingIcon} color="#1e88e5" /></StatButton></Grid>
        <Grid size={{ xs: 12, md: 2 }}><StatButton selected={statMode === "awaitingApproval"} onClick={() => setStatMode("awaitingApproval")}><StatsCard title="검수 대기" value={`${totals.awaitingApprovalCount}건`} icon={PendingActionsIcon} color="#8e44ad" /></StatButton></Grid>
        <Grid size={{ xs: 12, md: 2 }}><StatButton selected={statMode === "completed"} onClick={() => setStatMode("completed")}><StatsCard title="완료" value={`${totals.completedCount}건`} icon={FactCheckIcon} color="#27ae60" /></StatButton></Grid>
        <Grid size={{ xs: 12, md: 2 }}><StatButton selected={statMode === "overdue"} onClick={() => setStatMode("overdue")}><StatsCard title="기한 경과" value={`${totals.overdueCount}건`} icon={WarningIcon} color="#e74c3c" /></StatButton></Grid>
        <Grid size={{ xs: 12, md: 2 }}><StatsCard title="완료 금액" value={formatPrice(totals.totalRevenue)} icon={InventoryIcon} color="#34495e" /></Grid>
      </Grid>

      {loading ? <LoadingSkeleton variant="table" rows={8} /> : (
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>작업자</TableCell>
                  <TableCell>승인 상태</TableCell>
                  <TableCell>활동 지역</TableCell>
                  <TableCell align="right">총 배정</TableCell>
                  <TableCell align="right">진행 중</TableCell>
                  <TableCell align="right">검수 대기</TableCell>
                  <TableCell align="right">완료</TableCell>
                  <TableCell align="right">기한 경과</TableCell>
                  <TableCell align="right">완료율</TableCell>
                  <TableCell align="right">완료 금액</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((row) => {
                  const selected = String(selectedCollectorId || filters.collectorId) === String(row.collectorId);
                  return (
                    <TableRow
                      key={row.collectorId}
                      hover
                      selected={selected}
                      onClick={() => {
                        setSelectedCollectorId(String(row.collectorId));
                        setFilters((prev) => ({ ...prev, collectorId: String(row.collectorId) }));
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.collectorName || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.phoneNumber || row.email || "-"}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={COLLECTOR_STATUS_LABELS[row.accountStatus] || row.accountStatus || "-"} /></TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Tooltip title={row.serviceRegions || "활동 지역 없음"}>
                          <Typography variant="body2" noWrap>{row.serviceRegions || "-"}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{row.totalAssigned}</TableCell>
                      <TableCell align="right">{row.activeCount}</TableCell>
                      <TableCell align="right">{row.awaitingApprovalCount}</TableCell>
                      <TableCell align="right">{row.completedCount}</TableCell>
                      <TableCell align="right">
                        {Number(row.overdueCount || 0) > 0 ? <Chip size="small" color="error" variant="outlined" label={`${row.overdueCount}건`} /> : "0"}
                      </TableCell>
                      <TableCell align="right">{row.completionRate}%</TableCell>
                      <TableCell align="right">{formatPrice(row.totalRevenue)}</TableCell>
                    </TableRow>
                  );
                })}
                {summary.length === 0 && (
                  <TableRow><TableCell colSpan={10}><EmptyState message="표시할 작업자가 없습니다." /></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedCollector ? `${selectedCollector.collectorName} 상세 작업` : "상세 작업 목록"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filters.fromDate || "-"} ~ {filters.toDate || "-"} 기준으로 필터된 배정 작업입니다.
                  </Typography>
                </Box>
                {(selectedCollectorId || filters.collectorId) && (
                  <Button size="small" variant="outlined" onClick={() => {
                    setSelectedCollectorId("");
                    setFilters((prev) => ({ ...prev, collectorId: "" }));
                  }}>
                    전체 보기
                  </Button>
                )}
              </Stack>
              {displayedItems.length === 0 ? <EmptyState message="상세 작업이 없습니다." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>요청</TableCell>
                        <TableCell>작업자</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>날짜/시간</TableCell>
                        <TableCell>지역/주소</TableCell>
                        <TableCell>이용자</TableCell>
                        <TableCell>품목</TableCell>
                        <TableCell align="right">금액</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedItems.map((item) => (
                        <TableRow
                          key={item.marketLocationId}
                          hover
                          onClick={() => setDetailTarget(item)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>#{item.marketLocationId}</Typography>
                            {item.overdue && <Chip size="small" color="error" variant="outlined" label="기한 경과" />}
                          </TableCell>
                          <TableCell>{item.collectorName || "-"}</TableCell>
                          <TableCell>{getStatusChip(item.status)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.desiredDate || "-"}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.desiredTimeSlot || "-"}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2">{`${item.regionSido || "-"} ${item.regionSigungu || ""}`}</Typography>
                            <Tooltip title={`${item.address || ""} ${item.detailAddress || ""}`.trim()}>
                              <Typography variant="caption" color="text.secondary" noWrap>{item.address || "-"}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{item.userName || "-"}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Tooltip title={item.itemsText || "-"}>
                              <Typography variant="body2" noWrap>{item.itemsText || "-"}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{formatPrice(item.totalPrice)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
      <RequestDetailDialog location={detailTarget} onClose={() => setDetailTarget(null)} />
    </Box>
  );
}

export function DreamReportsPage() {
  const [tab, setTab] = useState("stats");
  return (
    <Box>
      <PageHeader title="들어드림 통계/리포트" description="운영 성과, 작업자 성과, 수출 품목 기록을 확인합니다." />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="stats" label="운영 통계" />
        <Tab value="exports" label="수출 품목 기록" />
      </Tabs>
      {tab === "stats" ? <MarketStatsTab /> : <MarketExportItemsTab />}
    </Box>
  );
}

export function DreamAdminIndexRedirect() {
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 1 }}>들어드림 운영 관제</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>운영 대시보드에서 오늘 처리할 요청을 확인하세요.</Typography>
        <Button component={Link} to="/admin/dream/operations" variant="contained">운영 대시보드로 이동</Button>
      </CardContent>
    </Card>
  );
}
